import { useMemo, useState } from "react";

import { type OldResource, type Resource } from "./types";

export const useResourcesSlideout = (resources: Resource[]) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resourceToDelete, setResourceToDelete] = useState<Resource | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Filter and sort resources based on search query and alphabetical order
  const filteredResources = useMemo(() => {
    let resourcesToSort = resources;

    // Filter by search query if provided
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      resourcesToSort = resources.filter((resource) => {
        const name = (resource.name || "").toLowerCase();
        const description = (resource.description || "").toLowerCase();
        const url = (resource.url || "").toLowerCase();

        return name.includes(query) || description.includes(query) || url.includes(query);
      });
    }

    // Sort resources alphabetically by name (case-insensitive)
    return resourcesToSort.sort((a, b) => {
      const nameA = (a.name || "").toLowerCase();
      const nameB = (b.name || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [resources, searchQuery]);

  const handleDeleteClick = (resource: Resource) => {
    setResourceToDelete(resource);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = (onDeleteResource?: (resource: OldResource) => void) => {
    if (resourceToDelete && onDeleteResource) {
      const resourceData = {
        description: resourceToDelete.description || "",
        id: resourceToDelete.id,
        name: resourceToDelete.name,
        url: resourceToDelete.url,
      };
      onDeleteResource(resourceData);
    }
    setDeleteDialogOpen(false);
    setResourceToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setResourceToDelete(null);
  };

  // Helper function to extract domain from URL for favicon
  const getFaviconUrl = (url: string) => {
    try {
      const domain = new URL(url).hostname;
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
    } catch {
      return null;
    }
  };

  return {
    deleteDialogOpen,
    filteredResources,
    getFaviconUrl,
    handleDeleteCancel,
    handleDeleteClick,
    handleDeleteConfirm,
    resourceToDelete,
    searchQuery,
    setSearchQuery,
  };
};
